import { useLocation } from "wouter";
import {
  ActionIcon,
  AppShell,
  Divider,
  NavLink,
  ScrollArea,
} from "@mantine/core";
import {
  IconKey,
  IconLogout,
  IconChevronRight,
  IconMessageCode,
  IconMessage2,
  IconMessageSearch,
  IconUsers,
  IconBuildingFactory2,
  IconExternalLink,
  IconChartAreaLine,
  IconReportMoney,
  IconTags,
  IconLockSquare,
  IconUserStar,
  IconBuildingFactory,
  IconServerCog,
  IconReceiptDollar,
  IconTools,
  IconUserPlus,
  IconX,
  IconThumbUp,
  IconArrowsTransferUp,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useStore } from "./store";
import UserAvatar from "./_Common/UserAvatar";

const baseUrl = import.meta.env.BASE_URL;

export default function Navbar({ toggle }) {
  const [location, setLocation] = useLocation();
  const authToken = useStore((state) => state.authToken);
  const setAuthToken = useStore((state) => state.setAuthToken);
  const profile = useStore((state) => state.profile);
  const setProfile = useStore((state) => state.setProfile);
  const savedSessions = useStore((state) => state.savedSessions);
  const switchToSession = useStore((state) => state.switchToSession);
  const removeSession = useStore((state) => state.removeSession);

  function MyNavLink(item) {
    return (
      <NavLink
        label={item.label}
        active={location.startsWith(`${baseUrl}${item.link}`)}
        key={item.label}
        leftSection={<item.icon stroke={1.5} />}
        rightSection={
          item.rightSection ?? <IconChevronRight size="0.8rem" stroke={1.5} />
        }
        onClick={(event) => {
          event.preventDefault();
          item.onClick ? item.onClick() : setLocation(`${baseUrl}${item.link}`);
          toggle();
        }}
      />
    );
  }

  return (
    <>
      {authToken ? (
        <>
          <AppShell.Section component={ScrollArea}>
            <MyNavLink
              label="Dashboard"
              link="dashboard"
              icon={IconChartAreaLine}
            />
            <MyNavLink
              label="Usage"
              link="usage"
              icon={IconReportMoney}
            />
            <MyNavLink label="Tag Costs" link="tag-costs" icon={IconTags} />
            <MyNavLink label="Playground" link="bulk" icon={IconMessage2} />
            <MyNavLink label="Panels" link="panels" icon={IconUsersGroup} />

            <Divider label="Automation" labelPosition="center" />
            <MyNavLink label="Flows" link="flows" icon={IconMessageCode} />
            <MyNavLink label="Environments" link="environments/promote" icon={IconArrowsTransferUp} />
            <MyNavLink label="Tools" link="tools" icon={IconTools} />
            <MyNavLink label="MCP Servers" link="mcp-servers" icon={IconServerCog} />
            <MyNavLink label="Logs" link="logs" icon={IconMessageSearch} />
            <MyNavLink label="Feedback" link="feedback" icon={IconThumbUp} />

            <Divider label="Settings" labelPosition="center" />
            <MyNavLink
              label="Tenant"
              link="tenant/profile"
              icon={IconBuildingFactory2}
            />
            <MyNavLink label="Users" link="users" icon={IconUsers} />
            <MyNavLink label="Models" link="models" icon={IconLockSquare} />
          </AppShell.Section>

          <AppShell.Section grow component={ScrollArea}>
            {profile?.userRole == "GlobalAdmin" && (
              <>
                <Divider label="Admin" />
                <MyNavLink
                  label="Provider Costs"
                  link="admin/provider-costs"
                  icon={IconReceiptDollar}
                />
                <MyNavLink
                  label="Users"
                  link="admin/users"
                  icon={IconUserStar}
                />
                <MyNavLink
                  label="Tenants"
                  link="tenants"
                  icon={IconBuildingFactory}
                />
                <MyNavLink label="Jobs" link="jobs" icon={IconServerCog} />
              </>
            )}
          </AppShell.Section>

          {profile && (
            <AppShell.Section>
              <NavLink
                rightSection={<IconExternalLink size={20} stroke={1.5} />}
                label="Support"
                target="_blank"
                href="https://66820da36394c.sleekplan.app/"
              ></NavLink>

              <NavLink
                href="/profile"
                label={profile.firstName ?? profile.emailAddress}
                onClick={(event) => {
                  event.preventDefault();
                  setLocation("/profile");
                  toggle();
                }}
                rightSection={<UserAvatar profile={profile} />}
              />
              {savedSessions.length > 0 && (
                <>
                  <Divider
                    label="Switch Account"
                    labelPosition="center"
                    my={4}
                  />
                  {savedSessions.map((session) => (
                    <NavLink
                      key={session.profile.id}
                      label={
                        session.profile.firstName ??
                        session.profile.emailAddress
                      }
                      description={session.profile.tenantName}
                      leftSection={
                        <UserAvatar profile={session.profile} />
                      }
                      rightSection={
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            removeSession(session.profile.id);
                          }}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        switchToSession(session.profile.id);
                        toggle();
                      }}
                    />
                  ))}
                </>
              )}
              <MyNavLink
                label="Add Account"
                icon={IconUserPlus}
                onClick={() => {
                  setLocation("/auth/login?addAccount");
                }}
              />
              <MyNavLink
                label="Logout"
                icon={IconLogout}
                onClick={() => {
                  setAuthToken(null);
                  setProfile(null);
                  setLocation("/");
                }}
              />
            </AppShell.Section>
          )}
        </>
      ) : (
        <>
          <MyNavLink label="Login" link="auth/login" icon={IconKey} />
          {savedSessions.length > 0 && (
            <>
              <Divider
                label="Saved Accounts"
                labelPosition="center"
                my={4}
              />
              {savedSessions.map((session) => (
                <NavLink
                  key={session.profile.id}
                  label={
                    session.profile.firstName ??
                    session.profile.emailAddress
                  }
                  description={session.profile.tenantName}
                  leftSection={
                    <UserAvatar profile={session.profile} />
                  }
                  rightSection={
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removeSession(session.profile.id);
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    switchToSession(session.profile.id);
                    toggle();
                  }}
                />
              ))}
            </>
          )}
        </>
      )}
    </>
  );
}
